'use client';

import { AccountInfo } from '@solana/web3.js';
import { PublicKey } from '@solana/web3.js';
import { useCallback, useEffect } from 'react';
import {
	VAULT_PROGRAM_ID,
	VaultClient,
	VaultDepositor,
	VaultDepositorAccount,
	calculateVaultDepositorFuel,
} from '@drift-labs/vaults-sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentAuthority from '../useCurrentAuthority';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { UiVaults } from 'src/constants/vaults';
import {
	getFuelOverflowAccountPublicKey,
	getUserStatsAccountPublicKey,
} from '@drift-labs/sdk';
import {
	fuelOverflowAccountDataDecoder,
	vaultDataDecoder,
	userStatsDataDecoder,
} from 'src/utils/programAccounts/decoders';

function decodeVaultDepositorAccounts(
	vaultDepositorAccounts: AccountInfo<Buffer>[],
	vaultClient: VaultClient
) {
	return vaultDepositorAccounts.map((vaultDepositorAccount) => {
		if (!vaultDepositorAccount) return null;
		const vaultDepositorAccountData =
			vaultClient.program.account.vaultDepositor.coder.accounts.decode(
				'vaultDepositor',
				vaultDepositorAccount.data
			);

		return vaultDepositorAccountData as VaultDepositor;
	});
}

export const useSyncVaultDepositorAccountsForCurrentAuthority = () => {
	const authority = useCurrentAuthority();
	const connection = useDriftStore((s) => s.connection.current);
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const setVaultsStore = useVaultsStore((s) => s.set);

	const VAULT_PUBKEYS = UiVaults.getNativeVaults().map(
		(vault) => vault.vaultPubkeyString
	);

	const getAndSetAllVaultDepositors = useCallback(async () => {
		if (!connection || !authority || !vaultClient) {
			setVaultsStore((s) => {
				s.authorityDepositedVaults = [];
			});
			return;
		}

		const vaultDepositorPubkeys = VAULT_PUBKEYS.map((vaultPubkey) => {
			return VaultDepositorAccount.getAddressSync(
				VAULT_PROGRAM_ID,
				new PublicKey(vaultPubkey),
				authority
			);
		});

		const vaultDepositors = (
			await connection.getMultipleAccountsInfo(vaultDepositorPubkeys)
		).filter(Boolean) as AccountInfo<Buffer>[];

		const decodedAccounts = decodeVaultDepositorAccounts(
			vaultDepositors,
			vaultClient
		);

		const existingVaultDepositorAccounts = decodedAccounts.filter(
			(acc) => !!acc
		);

		const vaultsAndUserStatsAccountPubkeys = existingVaultDepositorAccounts
			.map((vaultDepositor) => {
				const userStatsAccountPublicKey = getUserStatsAccountPublicKey(
					vaultClient.driftClient.program.programId,
					vaultDepositor.vault
				);
				const fuelOverflowAccountPublicKey = getFuelOverflowAccountPublicKey(
					vaultClient.driftClient.program.programId,
					vaultDepositor.vault
				);

				return [
					vaultDepositor.vault,
					userStatsAccountPublicKey,
					fuelOverflowAccountPublicKey,
				];
			})
			.flat();
		const numOfFetchedAccountsPerVault = 3;

		const vaultsAndUserStatsAccounts = await connection.getMultipleAccountsInfo(
			vaultsAndUserStatsAccountPubkeys
		);

		setVaultsStore((s) => {
			const vaultDepositorAndFuelEarnedList = [];

			for (let i = 0; i < existingVaultDepositorAccounts.length; i++) {
				const vaultDepositor = existingVaultDepositorAccounts[i];
				const vaultData =
					vaultsAndUserStatsAccounts[i * numOfFetchedAccountsPerVault];
				const vaultUserStatsAccountData =
					vaultsAndUserStatsAccounts[i * numOfFetchedAccountsPerVault + 1];
				const fuelOverflowAccountData =
					vaultsAndUserStatsAccounts[i * numOfFetchedAccountsPerVault + 2];

				if (!vaultData || !vaultUserStatsAccountData) {
					continue;
				}

				const vault = vaultDataDecoder(vaultData.data, vaultClient.program);
				const vaultUserStatsAccount = userStatsDataDecoder(
					vaultUserStatsAccountData.data,
					vaultClient.driftClient.program
				);
				const fuelOverflowAccount = fuelOverflowAccountData
					? fuelOverflowAccountDataDecoder(
							fuelOverflowAccountData.data,
							vaultClient.driftClient.program
					  )
					: undefined;

				const fuelEarned = calculateVaultDepositorFuel(
					vaultDepositor,
					vault,
					vaultUserStatsAccount,
					fuelOverflowAccount
				);

				vaultDepositorAndFuelEarnedList.push({
					vaultDepositor,
					fuelEarned,
					vault,
				});
			}

			s.authorityDepositedVaults = vaultDepositorAndFuelEarnedList;
		});
	}, [connection, authority, vaultClient]);

	useEffect(() => {
		getAndSetAllVaultDepositors();
	}, [getAndSetAllVaultDepositors]);
};
